import React from "react";
import {
    capitalize,
    Checkbox,
    Chip,
    ClickAwayListener,
    Popper,
    Paper,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Alert,
} from "@mui/material";
import {Toolbar, ToolbarButton} from "@mui/x-data-grid";
import TableChartIcon from '@mui/icons-material/TableChart';
import {
    GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
    FilterPanelTrigger,
    gridColumnLookupSelector,
    GridFilterListIcon,
    useGridApiContext,
    useGridSelector,
    ColumnsPanelTrigger,
} from "@mui/x-data-grid-premium";
import {observer} from "mobx-react-lite";
import {
    CAT_EQ_OPERATOR, CAT_NEQ_OPERATOR
} from "../../categorization/components/review-table/review-table-muix/filters/CategoryFilter.tsx";
import {useStores} from "../../../stores";
import Tooltip from "@mui/material/Tooltip";
import Stack from "@mui/material/Stack";
import {NavigateNext, Refresh, ViewColumn, WidthWide} from "@mui/icons-material";

/**
 * Taken from https://mui.com/x/react-data-grid/components/toolbar/#filter-bar
 */
export const GRToolbar: React.FC = observer(() => {
    const {p, goldenRecordStore} = useStores()
    const apiRef = useGridApiContext();
    const tableController = goldenRecordStore.grPageController.tableController;
    const filterController = goldenRecordStore.grPageController.filterController;
    const columns = useGridSelector(apiRef, gridColumnLookupSelector);
    // noinspection TypeScriptValidateTypes
    const groupingPanelTriggerRef = React.useRef<HTMLButtonElement>(null);
    const [groupingPanelOpen, setGroupingPanelOpen] = React.useState(false);

    return <Toolbar>
        <Tooltip title="Grouping">
            <ToolbarButton
                ref={groupingPanelTriggerRef}
                aria-describedby="grouping-panel"
                onClick={() => setGroupingPanelOpen(!groupingPanelOpen)}
            >
                <TableChartIcon fontSize="small" className="m-1"/>
            </ToolbarButton>
        </Tooltip>
        <Popper
            open={groupingPanelOpen}
            anchorEl={groupingPanelTriggerRef.current}
            placement="bottom-start"
            id="grouping-panel"
        >
            <ClickAwayListener onClickAway={() => setGroupingPanelOpen(false)}>
                <Paper elevation={8} style={{maxHeight: '50vh', overflow: 'scroll'}}>
                    <List>
                        {tableController.tableColumns.filter(col => !!col.groupable).map(col => {
                                const checked = tableController.rowGroupingModel.includes(col.field);
                                return <ListItem key={col.field} className='p-0'>
                                    <ListItemButton role='button' dense className='px-0.5 py-0'
                                                    onClick={(_e) => checked ?
                                                        tableController.removeGroupingCol(col.field) :
                                                        tableController.addGroupingCol(col.field)}>
                                        <ListItemIcon>
                                            <Checkbox
                                                edge="start"
                                                checked={tableController.rowGroupingModel.includes(col.field)}
                                                tabIndex={-1}
                                                slotProps={{input: {'aria-labelledby': 'checkbox-list-label-' + col.field}}}
                                            />
                                        </ListItemIcon>
                                        <ListItemText id={col.field} primary={col.headerName}/>
                                    </ListItemButton>
                                </ListItem>
                            }
                        )}
                    </List>
                </Paper>
            </ClickAwayListener>
        </Popper>
        <Stack direction='row' divider={<NavigateNext fontSize='small' className='my-auto'/>} className='me-4'>
            {tableController.rowGroupingModel.map((grouping_col) => {
                const headerName = columns[grouping_col]?.headerName ?? '';
                return <Chip
                    key={grouping_col}
                    label={headerName}
                    onDelete={() => tableController.removeGroupingCol(grouping_col)}
                />;
            })}
        </Stack>

        <Tooltip title="Filters">
            <FilterPanelTrigger render={<ToolbarButton/>}>
                <GridFilterListIcon fontSize="small"/>
            </FilterPanelTrigger>
        </Tooltip>

        <Stack direction='row' sx={{gap: 0.5, flex: 1}}>
            {filterController.gridFilterModel.items.filter(f => f?.value).map((filter) => {
                const column = columns[filter.field];
                const fieldName = (
                    filter.field === GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD
                        ? p.supplierDataName // Columns might not be loaded immediately, so we use the profileStore here...
                        : (column?.headerName ?? filter.field)
                );

                let operator = '';
                let label: string;

                if (filter.operator === CAT_EQ_OPERATOR.operatorValue) {
                    // noinspection TypeScriptUnresolvedReference
                    operator = apiRef.current.getLocaleText('filterOperatorEquals');
                    label = `${fieldName} ${operator} previous`;
                } else if (filter.operator === CAT_NEQ_OPERATOR.operatorValue) {
                    // noinspection TypeScriptUnresolvedReference
                    operator = apiRef.current.getLocaleText('filterOperatorDoesNotEqual');
                    label = `${fieldName} ${operator} previous`;
                } else if (filter.operator === 'between') {
                    // To avoid missing translation
                    // See https://github.com/mui/mui-x/issues/13217
                    const lowerBound = String(Number(filter.value?.at(0) ?? 0) * 100) + ' <= ';
                    const upperBound = ' <= ' + String(Number(filter.value?.at(1) ?? 0) * 100);
                    label = `${filter.value?.at(0) ? lowerBound : ''}${fieldName}${filter.value?.at(1) ? upperBound : ''}`;
                } else {
                    const lookup = `filterOperator${capitalize(filter.operator)}` as 'filterOperatorContains';
                    // noinspection TypeScriptUnresolvedReference
                    operator = apiRef.current.getLocaleText(lookup);
                    label = `${fieldName} ${operator} ${filter.value ?? ''}`;
                }

                return <Chip
                    key={filter.id}
                    label={label}
                    onDelete={() => filterController.removeFilter(filter.id)}
                    className='ms-1'
                />;
            })}
        </Stack>

        {(tableController.sortModel.length === 0) && <Alert severity='info'>
            No sorting is specified!&nbsp;
            {((tableController.defaultSortModel?.length ?? 0) > 0) && <span>Default is
                {tableController.defaultSortModel?.map(si => <b>
                    {tableController.tableColumns.find(c => c.field === si.field)?.headerName ?? ''}
                    &nbsp;
                    {si.sort === 'desc' ? 'Descending' : 'Ascending'}
                </b>)}
            </span>}
        </Alert>}

        <Tooltip title="Auto size columns">
            <ToolbarButton onClick={() => tableController.setAutoResize(!tableController.autoResize)}>
                <WidthWide fontSize='small' color={tableController.autoResize ? 'primary' : 'disabled'}/>
            </ToolbarButton>
        </Tooltip>

        <ColumnsPanelTrigger aria-label='gr-column-panel-trigger' render={<ToolbarButton/>}>
            <ViewColumn fontSize="small"/>
        </ColumnsPanelTrigger>

        <Tooltip title="Refresh data">
            <ToolbarButton onClick={() => tableController.resetRowState(true)}>
                <Refresh fontSize="small"/>
            </ToolbarButton>
        </Tooltip>
    </Toolbar>;
});